import { Facebook, Instagram, Phone, Mail, MapPin } from "lucide-react";
import { BUSINESS } from "../data/site";
import { scrollTo } from "./Nav";

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, tiktok: TikTokIcon };

const QUICK_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Menu", href: "#menu" },
  { label: "Contact", href: "#contact" },
  { label: "Order Online", href: "#order" },
];

export const SocialIcons = ({ className = "" }) => (
  <div className={`flex gap-3 ${className}`}>
    {BUSINESS.socials.map((s) => {
      const Icon = SOCIAL_ICONS[s.id];
      return (
        <a
          key={s.id}
          data-testid={`social-${s.id}`}
          href={s.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={s.label}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-all duration-300 hover:border-ember hover:bg-ember hover:text-cream"
        >
          <Icon size={17} />
        </a>
      );
    })}
  </div>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-cream/10 bg-[#101012] text-cream">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 md:grid-cols-12 md:px-10 md:py-20">
        <div className="md:col-span-4">
          <img
            src="/logo-badge.jpg"
            alt="Croudace Pizza & Burger logo"
            data-testid="footer-logo"
            className="h-20 w-20 rounded-full object-cover ring-2 ring-gold/40"
          />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-cream/50">
            Roman-style pizza. Smashed burgers. Your local pizza &amp; burger shop in New Lambton Heights.
          </p>
          <SocialIcons className="mt-8" />
        </div>
        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cream/40">Contact</p>
          <ul className="mt-5 space-y-4 text-sm text-cream/70">
            <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-ember" />{BUSINESS.address}</li>
            <li>
              <a href={BUSINESS.phoneHref} data-testid="footer-phone" className="flex gap-3 transition-colors hover:text-gold">
                <Phone size={16} className="mt-0.5 shrink-0 text-ember" />{BUSINESS.phoneDisplay}
              </a>
            </li>
            <li>
              <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email" className="flex gap-3 break-all transition-colors hover:text-gold">
                <Mail size={16} className="mt-0.5 shrink-0 text-ember" />{BUSINESS.email}
              </a>
            </li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cream/40">Opening Hours</p>
          <ul className="mt-5 space-y-2 text-sm">
            {BUSINESS.hours.map((h) => (
              <li key={h.day} className="flex justify-between gap-4">
                <span className="text-cream/70">{h.day}</span>
                <span className={h.time === "Closed" ? "text-cream/35" : "text-cream/70"}>{h.time}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cream/40">Quick Links</p>
          <ul className="mt-5 space-y-3">
            {QUICK_LINKS.map((l) => (
              <li key={l.href}>
                <button
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => scrollTo(l.href)}
                  className="text-sm text-cream/70 transition-colors hover:text-gold"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-2 px-5 py-6 text-xs text-cream/40 md:flex-row md:items-center md:px-10">
          <p>© {new Date().getFullYear()} Croudace Pizza &amp; Burger. All rights reserved.</p>
          <p className="uppercase tracking-[0.25em]">New Lambton Heights · NSW · Australia</p>
        </div>
      </div>
    </footer>
  );
}
